import axios from 'axios'
import { getToken } from '@/utils/auth'
import { ROOT_CONFIG } from '@/utils/config'
import store from '@/store/index'
import qs from 'qs'
import { getALivableRedirectPatch } from '@/router/helpers'
import { showErrorMessage } from '../helpers/messageHelper'
import { THttpClient } from '../types/api/HttpClient.model'
import { TRequestConfig } from '../types/api/RequestConfig.model'
import router from '@/router'
import '../trace-buffer/debug-trace'

import { initDB, persistTraces } from '../trace-buffer/storage-indexeddb'
import { traceBuffer } from '@/core/trace-buffer/trace-buffer'

let batchCounter: number = 0
const BATCH_SIZE: number = 0

let dbInitPromise: Promise<void> | null = null

function ensureDBInit(): Promise<void> {
  if (!dbInitPromise) {
    dbInitPromise = initDB().catch((err) => {
      console.error('❌ Ошибка инициализации IndexedDB:', err)
      dbInitPromise = null
      throw err
    })
  }
  return dbInitPromise
}

export class HttpClient {
  client: THttpClient
  baseURL = ROOT_CONFIG.api_url_v2

  constructor(defaultHeaders = {}) {
    ensureDBInit()

    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 155000,
      headers: {
        'Content-Type': 'application/json',
        ...defaultHeaders,
      },
    })

    this.client.interceptors.request.use(
      (config) => {
        if (getToken()) {
          config.headers.Authorization = getToken()
        }
        config.paramsSerializer = (p) => {
          return qs.stringify(p, { arrayFormat: 'repeat' })
        }
        return config
      },
      (error) => Promise.reject(error),
    )

    this.client.interceptors.response.use(
      async (response) => {
        const traceId = response.headers['x-trace-id'] as string | undefined

        if (traceId) {
          traceBuffer.add(traceId)
          console.log('➕ Добавлен trace:', traceId, '| Всего в буфере:', traceBuffer.getSize())

          if (++batchCounter >= BATCH_SIZE) {
            const allTraces = traceBuffer.getAll()
            const unsaved = allTraces.filter((t) => !t.persisted)

            try {
              await ensureDBInit()
              await persistTraces(unsaved)

              unsaved.forEach((t) => {
                t.persisted = true
              })
              batchCounter = 0
            } catch (err) {
              console.error('❌ Не удалось сохранить:', err)
            }
          }
        }

        return response
      },
      (error) => {
        const traceId = error.response?.headers?.['x-trace-id']

        if (traceId) {
          traceBuffer.add(traceId)
          console.log('➕ Добавлен trace (error):', traceId)
        }

        if (error.message.includes('403')) {
          showErrorMessage('Доступ запрещен', { duration: 5000 })
          store.dispatch('userStore/logoutUser')
          router.push('/login')
        }

        if (error.message.includes('401')) {
          const getRedirect = getALivableRedirectPatch()
          console.log({ getRedirect })

          if (getRedirect === 'Login' || !getRedirect) {
            showErrorMessage('Почта или пароль введены не верно', { duration: 5000 })
            store.dispatch('userStore/logoutUser')
            router.push('/login')
          }

          router.push({ name: getRedirect as string })
        }
        return Promise.reject(error)
      },
    )
  }

  async get(url: string, config?: TRequestConfig) {
    const response = await this.client!.get(url, config)
    return response.data
  }

  async post(url: string, data?: any, config?: TRequestConfig) {
    const response = await this.client.post(url, data, config)
    return response.data
  }

  async put(url: string, data?: any, config?: TRequestConfig) {
    const response = await this.client.put(url, data, config)
    return response.data
  }

  async delete(url: string, config?: TRequestConfig) {
    const response = await this.client.delete(url, config)
    return response.data
  }

  async patch(url: string, data?: any, config?: TRequestConfig) {
    const response = await this.client.patch(url, data, config)
    return response.data
  }

  async postExel(url: string, data: any, separator: string) {
    let queryParams = ''

    if (!Object.values(data).includes(undefined)) {
      queryParams = _objectToQueryParams(data)
    }

    const urlV2 = this.baseURL + url + queryParams

    const response = await this.client.post(
      '/csv/create-from-path',
      {},
      {
        params: {
          url: urlV2,
          separator,
        },
      },
    )

    return response.data
  }
}

const _objectToQueryParams = (data: Record<string, any>) => {
  const queryParams = Object.keys(data)
    .map((key) => encodeURIComponent(key) + '=' + encodeURIComponent(data[key]))
    .join('&')
  return '?' + queryParams
}
